import { clientConfig } from 'pos-client/clientConfig.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import type { BusinessOrganisationModel } from 'pos-shared/models/businessOrganisationSchema.ts';
import type {
  CountryModel,
  CustomerFeatureFlagsModel,
  CustomerValidAffiliationNamesType,
  FeatureConfigModel,
  FleetEnabledModel,
  IdNameStaticConfigModel,
  JobStatusOptionsModel,
  LocaleType,
  NameValueStaticConfigModel,
  OrderStatusOptionsModel,
  ProductGroupModel,
  PurchaseOrderNumberFormatType,
  StaticConfigDataModel,
  StockEnabledForOrdersModel,
  TimeZoneModel,
} from 'pos-shared/models/staticConfigSchema.ts';
import type { ReturnReasonCodesConfigModel } from 'pos-shared/models/stockSchema.ts';
import { time } from 'pos-shared/utils/secondsHelper.ts';

// the underlying hook with caching
const useStaticConfig = <T,>(key: keyof StaticConfigDataModel) => {
  const response = trpc.staticConfig.get.useQuery(key, {
    cacheTime: time.oneDayInSecs,
    staleTime: time.oneDayInSecs,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });

  const { data, isLoading, isFetching, ...rest } = response;

  return {
    staticConfigData: data as T,
    isLoadingStaticConfig: isLoading,
    isFetchingStaticConfig: isFetching,
    ...rest,
  };
};

export const useProductGroupsStaticConfigData = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig('productGroups');

  return {
    productGroups: staticConfigData as ProductGroupModel[],
    isLoadingProductGroups: isLoadingStaticConfig,
    ...rest,
  };
};

export const useBusinessOrganisationStaticConfigData = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } = useStaticConfig(
    'businessOrganisation'
  );

  return {
    businessOrganisation: staticConfigData as BusinessOrganisationModel,
    isLoadingBusinessOrganisation: isLoadingStaticConfig,
    ...rest,
  };
};

export const useCountryStaticConfigData = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig('countryOptions');

  return {
    countryOptions: staticConfigData as CountryModel[],
    isLoadingCountryOptions: isLoadingStaticConfig,
    ...rest,
  };
};

export const useOrderStatusConfigData = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig('orderStatusOptions');

  return {
    orderStatusOptions: staticConfigData as OrderStatusOptionsModel,
    isLoadingOrderStatusOptions: isLoadingStaticConfig,
    ...rest,
  };
};

export const useTimeZoneConfigData = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig('timeZones');

  return {
    timeZones: staticConfigData as TimeZoneModel[],
    isLoadingTimeZones: isLoadingStaticConfig,
    ...rest,
  };
};

export const useStockTakeProfileCycleDayConfigData = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } = useStaticConfig(
    'stockTakeProfileCycleDayOptions'
  );

  return {
    stockTakeProfileCycleDayOptions:
      staticConfigData as NameValueStaticConfigModel[],
    isLoadingStockTakeProfileCycleDayOptions: isLoadingStaticConfig,
    ...rest,
  };
};

export const useFulfilmentTypeConfigData = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig('fulfilmentTypes');

  return {
    fulfilmentTypes: staticConfigData as IdNameStaticConfigModel[],
    isLoadingFulfilmentTypes: isLoadingStaticConfig,
    ...rest,
  };
};

export const useJobStatusConfigData = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<JobStatusOptionsModel>('jobStatusOptions');

  return {
    jobStatusOptions: staticConfigData,
    isLoadingJobStatusOptions: isLoadingStaticConfig,
    ...rest,
  };
};

export const useFleetOptionsConfigData = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<FleetEnabledModel>('fleetOptions');

  return {
    fleetEnabled: staticConfigData?.enabled,
    isLoadingFleetEnabled: isLoadingStaticConfig,
    ...rest,
  };
};

export const useStockEnabledForOrders = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<StockEnabledForOrdersModel>('stockEnabledForOrdersOptions');

  return {
    stockEnabledForOrders: staticConfigData === 'true',
    isLoadingStockEnabledForOrders: isLoadingStaticConfig,
    ...rest,
  };
};

export const useOneSourceEnabled = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<FeatureConfigModel>('oneSourceEnabled');

  return {
    oneSourceEnabled: staticConfigData?.isEnabled,
    isLoadingOneSourceEnabled: isLoadingStaticConfig,
    ...rest,
  };
};

export const usePurchaseOrderDraftEnabled = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<FeatureConfigModel>('purchaseOrderDraftEnabled');

  return {
    purchaseOrderDraftEnabled: staticConfigData?.isEnabled,
    isLoadingPurchaseOrderDraftEnabled: isLoadingStaticConfig,
    ...rest,
  };
};

export const useCallOffPurchaseOrdersEnabled = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<FeatureConfigModel>('callOffPurchaseOrdersEnabled');

  return {
    callOffPurchaseOrdersEnabled: staticConfigData?.isEnabled ?? false,
    isLoadingCallOffPurchaseOrdersEnabled: isLoadingStaticConfig,
    ...rest,
  };
};

export const useOneTimeSupplierEnabled = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<FeatureConfigModel>('oneTimeSupplierEnabled');

  return {
    oneTimeSupplierEnabled: staticConfigData?.isEnabled,
    isLoadingOneTimeSupplierEnabled: isLoadingStaticConfig,
    ...rest,
  };
};

export const usePurchaseOrderNumberFormats = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<PurchaseOrderNumberFormatType>(
      'purchaseOrderNumberFormats'
    );

  return {
    purchaseOrderNumberFormats: staticConfigData,
    isLoadingPurchaseOrderNumberFormats: isLoadingStaticConfig,
    ...rest,
  };
};

export const useCustomerValidAffiliationNames = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<CustomerValidAffiliationNamesType>('validAffiliationNames');

  return {
    validAffiliationNames: staticConfigData?.values ?? [],
    isLoadingValidAffiliationNames: isLoadingStaticConfig,
    ...rest,
  };
};

export const useStockReturnReasons = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<string[]>('stockReturnReasons');

  return {
    stockReturnReasons: staticConfigData ?? [],
    isLoadingStockReturnReasons: isLoadingStaticConfig,
    ...rest,
  };
};

interface UseStockReturnCodesOptions {
  enabled?: boolean;
}

export const useStockReturnCodes = (
  options: UseStockReturnCodesOptions = {}
) => {
  const { enabled = true } = options;

  const {
    data,
    isLoading: isLoadingStockReturnCodes,
    isFetching: isFetchingStockReturnCodes,
    ...rest
  } = trpc.getReturnReasonCodes.useQuery(undefined, { enabled });

  return {
    stockReturnCodesConfig: data as ReturnReasonCodesConfigModel | undefined,
    stockReturnCodes: data?.reasonCodes ?? [],
    isStockReturnCodesEnabled: data?.isEnabled ?? false,
    isLoadingStockReturnCodes,
    isFetchingStockReturnCodes,
    ...rest,
  };
};

export const useLocale = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<LocaleType>('locale');

  return {
    locale: staticConfigData?.value ?? clientConfig.locale,
    isLoadingLocale: isLoadingStaticConfig,
    ...rest,
  };
};

export const useCustomerFeatureFlags = () => {
  const { staticConfigData, isLoadingStaticConfig, ...rest } =
    useStaticConfig<CustomerFeatureFlagsModel>('customerFeatureFlags');

  // TODO(cash-banking): remove after the demo (~next week) — temp visibility into BE feature-flags.
  // eslint-disable-next-line no-console
  console.log(
    'API response - customer feature flags:',
    staticConfigData,
    'endOfDayEnabled:',
    staticConfigData?.endOfDayEnabled,
    'cashAndBankingEnabled:',
    staticConfigData?.cashAndBankingEnabled
  );

  return {
    featureFlags: staticConfigData ?? {},
    // Store Accounting sub-nav flags ride on this same feature-flags response (BE to add the two
    // keys, alongside useEmployeeId). Missing → default true so the sub-items show until BE ships;
    // flip to `?? false` for BE-driven hide-by-default.
    // Intent — endOfDayEnabled: show BSRO / hide MyCar
    // cashAndBankingEnabled: hide BSRO / show MyCar.
    endOfDayEnabled: staticConfigData?.endOfDayEnabled ?? false,
    cashAndBankingEnabled: staticConfigData?.cashAndBankingEnabled ?? false,
    isLoadingFeatureFlags: isLoadingStaticConfig,
    ...rest,
  };
};
